// IAMMUZIK brand marks — the gold crown is traced directly from the user's
// logo photo (public/crown.svg), preserved as-is. Wordmarks use textured gold.

import { useId } from 'react';

const GOLD_STOPS = [
  { o: '0%', c: '#f2d36c' },
  { o: '42%', c: '#c9a227' },
  { o: '72%', c: '#8a6d1a' },
  { o: '100%', c: '#e8c66a' },
];

function useGoldIds(prefix: string) {
  const id = useId().replace(/:/g, '');
  return { grad: `${prefix}G${id}`, filt: `${prefix}F${id}` };
}

function GoldDefs({ grad, filt }: { grad: string; filt: string }) {
  return (
    <defs>
      <linearGradient id={grad} x1="0" y1="0" x2="1" y2="1">
        {GOLD_STOPS.map((s) => (
          <stop key={s.o} offset={s.o} stopColor={s.c} />
        ))}
      </linearGradient>
      <filter id={filt} x="-12%" y="-12%" width="124%" height="124%">
        <feTurbulence type="fractalNoise" baseFrequency="0.78" numOctaves="2" seed="7" result="n" />
        <feDisplacementMap in="SourceGraphic" in2="n" scale="0.7" />
      </filter>
    </defs>
  );
}

// The crown from the user's logo photo (traced to a crisp vector in crown.svg).
// viewBox 0 0 100 77.75, so height = size * 0.7775.
export function Crown({ size = 40, className = '' }: { size?: number; className?: string }) {
  return (
    <img
      src="/crown.svg"
      alt=""
      aria-hidden="true"
      className={className}
      style={{ width: size, height: size * 0.7775, display: 'block' }}
    />
  );
}

// Monogram: the IAM mark traced directly from the user's monogram photo
// (public/monogram.svg — crown atop interlocking IAM). viewBox 0 0 100 119.62,
// so height = size * 1.1962.
export function Monogram({ size = 26 }: { size?: number }) {
  return (
    <img
      src="/monogram.svg"
      alt="IAM monogram"
      role="img"
      aria-label="IAMMUZIK monogram"
      style={{ width: size, height: size * 1.1962, display: 'block' }}
    />
  );
}

// Small diamond ornament between two thin gold rules (from the reference lockup).
export function DiamondDivider({ width = 160 }: { width?: number }) {
  const { grad, filt } = useGoldIds('div');
  return (
    <svg width={width} height="10" viewBox="0 0 160 10" role="presentation" aria-hidden="true">
      <GoldDefs grad={grad} filt={filt} />
      <line x1="4" y1="5" x2="68" y2="5" stroke={`url(#${grad})`} strokeWidth="1" />
      <rect x="74" y="1.5" width="7" height="7" transform="rotate(45 77.5 5)" fill={`url(#${grad})`} />
      <line x1="86" y1="5" x2="156" y2="5" stroke={`url(#${grad})`} strokeWidth="1" />
    </svg>
  );
}

// Hand-drawn gold brush tail — a signature swoosh underlining the wordmark
// and curling up to the right (echoes the long tail off the "K").
export function BrushTail({ width = 260 }: { width?: number }) {
  const { grad, filt } = useGoldIds('tail');
  return (
    <svg width={width} height="22" viewBox="0 0 260 22" role="presentation" aria-hidden="true">
      <GoldDefs grad={grad} filt={filt} />
      <path
        d="M6 14 Q 70 2 150 11 T 248 6"
        stroke={`url(#${grad})`}
        strokeWidth="2.4"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

// Compact brand logo for headers, sidebars, footers — the full traced logo
// (crown + IAMMUZIK brush wordmark + underline + dot) at a small size.
// viewBox 0 0 100 62.61, so width = height * 1.597.
export function BrandLogo({ height = 38, className = '' }: { height?: number; className?: string }) {
  return (
    <img
      src="/logo.svg"
      alt="IAMMUZIK"
      className={className}
      height={height}
      style={{ display: 'block', height, width: 'auto', maxWidth: '100%' }}
    />
  );
}

// Full brand lockup for the landing hero: two stacked parts —
//   top:    the user's exact traced photo logo (crown + "IAMMUZIK" brush
//           wordmark + sweeping underline + dot, from logo.svg) + diamond divider
//   bottom: traced IAM monogram
// The wordmark letters are kept exactly as the photo — no hyphen, since the
// brush letters flow as one connected stroke (matching the source logo).
// logo.svg viewBox 0 0 100 62.61, so height = width * 0.6261.
export function LogoLockup({ width = 360 }: { width?: number }) {
  return (
    <div className="logo-lockup" aria-label="IAMMUZIK">
      <img
        src="/logo.svg"
        alt="IAMMUZIK"
        width={width}
        height={Math.round(width * 0.6261)}
        style={{ display: 'block', maxWidth: '100%', height: 'auto' }}
      />
      <DiamondDivider width={190} />
      <div className="lockup-monogram">
        <Monogram size={44} />
      </div>
    </div>
  );
}
